import { useState } from "react";
import {
  BookOpen,
  LayoutDashboard,
  Phone,
  Users,
  Target,
  BarChart3,
  Trophy,
  ShieldOff,
  Activity,
  PhoneCall,
  Settings,
  ChevronDown,
  ChevronRight,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Shield,
  Search,
} from "lucide-react";
import { Badge } from "../components/ui/badge";

const sections = [
  {
    id: "overview",
    icon: BookOpen,
    title: "What is Callintell?",
    content: `Callintell is an AI-powered call intelligence platform built for Pay Per Call businesses. It connects to your Ringba account, automatically imports call recordings, and uses advanced AI (GPT-4o-mini + Gemini) to classify every call into categories like Valid, Fake/Spam, Dead, Adult, Non-US Accent, and more.

The goal is simple: help you identify and block fraudulent traffic so you only pay for quality calls.

How it works:
1. Ringba sends call data to Callintell every 5 minutes (calls over 90 seconds)
2. AI transcribes the recording using Gemini (primary) or Whisper (fallback)
3. GPT-4o-mini analyzes the CALLER's speech only (ignores IVR/agent)
4. Post-processing safety nets catch anything the AI missed
5. Results appear on your dashboard within minutes`,
  },
  {
    id: "dashboard",
    icon: LayoutDashboard,
    title: "Dashboard",
    content: `Your command center. Shows a real-time overview of call quality.

What you see:
- Total Calls, Valid Calls, Fake Calls, Non-US Calls — today's numbers at a glance
- Quality Score — percentage of valid calls out of total analyzed calls
- Trend Charts — daily call volume and category breakdown over time
- Publisher Risk — top publishers ranked by fraud score
- Recent Activity — latest calls and their classifications

The date filter at the top controls what time period you're viewing. Default is "Today".

Tip: If the numbers look wrong, make sure you're looking at the right date range. Calls from yesterday won't show under "Today".`,
  },
  {
    id: "call-analysis",
    icon: Phone,
    title: "Call Analysis",
    content: `The heart of the system. Every imported call appears here with its AI classification.

Filter Tabs:
- All — Every call regardless of category
- Valid — Legitimate service calls (caller had genuine intent)
- Fake — Spam calls + Dead calls combined
- Non-US — Callers with detected South Asian/Filipino accents
- IVR/Robot — Caller themselves is an automated system (not a human reaching an IVR)
- Adult — Calls with adult/sexual content detected
- Buy/Sell — Callers trying to buy/sell products instead of requesting services
- Pending — Calls waiting to be analyzed

Search Box:
Type any phone number to search across ALL dates (bypasses date filter). The "Searching all dates" indicator confirms this.

View Report:
Click any call to see the full AI analysis — transcript, classification, confidence score, accent detection breakdown, and the actual audio recording.

Re-Analyze:
If you disagree with a classification, click the refresh icon to re-run AI analysis on that call.

Mark as Valid:
Click the shield icon to manually override a classification to "Valid". This protects the call from future re-analysis and migrations.`,
  },
  {
    id: "categories",
    icon: Shield,
    title: "Call Categories Explained",
    content: `Every call gets ONE category. Here's what each means:

Valid Service Call (Green):
The caller had genuine intent for a home service — plumbing, HVAC, roofing, pest control, etc. Even if no appointment was scheduled, if the caller showed real interest, it's valid.

Spam / Fake Call (Red):
The caller had zero relevant intent. Off-topic conversation, gibberish, telemarketing, or the caller said almost nothing meaningful.

Dead Call (Gray):
The caller produced ABSOLUTELY ZERO vocal output. Complete silence from the caller's end. If the caller said even one word ("yes", "hello"), it's NOT dead — it would be Spam instead.

Non-US Accent (Orange):
The caller has a detected South Asian accent (Indian, Pakistani, Bangladeshi, Filipino). This uses a multi-model pipeline: GPT text analysis + Gemini audio phoneme/prosody detection. Only flags South Asian accents — Spanish, European, AAVE accents are NOT flagged.

Adult Call (Red - Blocked):
The caller mentioned adult/sexual content. This has the HIGHEST priority — if any adult keyword is detected, it overrides all other categories. Also includes Gemini audio detection for non-verbal adult content (moaning, sounds).

IVR/Robot Call:
The CALLER themselves is automated — a robodial system. Note: a human caller reaching a business IVR menu is NOT an IVR call.

Buy/Sell Intent (Yellow):
The caller wanted to buy or sell products rather than request a service. Example: "I want to buy some windows" vs "I need my windows replaced".`,
  },
  {
    id: "did-status",
    icon: PhoneCall,
    title: "DID Status",
    content: `Track which DID (tracking) numbers are actually generating calls.

How Status is Determined:
- Active (Green) — The DID received at least 1 call in the selected date range (per Ringba data)
- Non-Active (Red) — Zero calls in the selected date range

Smart Tags:
- New — DID purchased less than 7 days ago
- Idle (Amber) — 7-30 days old with no calls yet
- Old & Dead (Red) — Over 30 days old with zero calls — consider deallocating
- Healthy (Green) — Active with call traffic
- Low Activity (Gray) — Ringba shows some activity but calls were under 90 seconds (not imported for analysis)

Date Filter:
Change the date range to check activity over different periods. "Last 30 Days" is default. Switch to "Last 6 Months" to see the full picture.

Calls Column:
Shows the number of analyzed calls (over 90 seconds) from your local database. A dash "—" means the DID had Ringba activity but all calls were short (under 90 seconds).

Tip: Sort by Non-Active first (default) to quickly identify numbers you're paying for but getting zero value from.`,
  },
  {
    id: "publisher-report",
    icon: Users,
    title: "Publisher Report",
    content: `See how each publisher is performing across all their calls.

Risk Levels:
- Trusted — Less than 20% fake calls
- Monitor — 20-49% fake calls
- High Risk — 50%+ fake calls

Important: "Fake" includes both Spam calls AND Dead calls.

The report shows: total calls, valid calls, problem calls, fake percentage, and risk status for each publisher. Use this to identify which publishers are sending low-quality traffic.

Tip: Publishers with very few total calls (like 2) can appear "High Risk" from just 1 fake call. Consider the total volume when evaluating.`,
  },
  {
    id: "publisher-ranking",
    icon: Trophy,
    title: "Publisher Ranking",
    content: `Publishers ranked by performance. Shows who's generating the most valid calls and who's sending the most problems.

Use this to quickly compare publishers side by side and make decisions about who to keep, pause, or remove.`,
  },
  {
    id: "publisher-activity",
    icon: Activity,
    title: "Publisher Activity (Admin)",
    content: `Detailed activity tracking for each publisher. Shows call patterns over time — when they're active, call volume trends, and any sudden changes in behavior.

Useful for detecting when a previously good publisher starts sending bad traffic.`,
  },
  {
    id: "publisher-exclusions",
    icon: ShieldOff,
    title: "Publisher Exclusions (Admin)",
    content: `Exclude specific publishers from your reports and call analysis. Excluded publishers' calls won't appear in your dashboard stats or call analysis page.

This is useful for filtering out test publishers or partners you've already dealt with, so they don't pollute your data.`,
  },
  {
    id: "action-center",
    icon: Target,
    title: "Action Center",
    content: `Your fraud response headquarters. Take action on problem callers and publishers.

What you can do:
- Block Callers — Block specific phone numbers from calling again (syncs to Ringba)
- Pause Publishers — Temporarily pause a publisher's traffic (syncs to Ringba)
- Auto-Block — System automatically blocks callers flagged as spam, adult, or dead
- Auto-Pause — System automatically pauses publishers on first adult call detection

All actions sync directly to your Ringba account in real-time. When you block a caller here, they're blocked in Ringba too.`,
  },
  {
    id: "analytics",
    icon: BarChart3,
    title: "Analytics",
    content: `Deep-dive charts and trends across your call data.

View call volume over time, category distribution, publisher performance trends, and quality metrics. Use the date range selector to analyze specific periods.

This is where you go to understand long-term patterns — not just today's calls, but how things are trending over weeks and months.`,
  },
  {
    id: "date-filters",
    icon: Search,
    title: "Date Filters & Timezone",
    content: `All dates and times in Callintell are in EST (Eastern Time) — matching Ringba's timezone.

Date Presets Available:
- Today — Current day only
- Yesterday — Previous day
- This Week — Sunday to today
- Last Week — Previous full week
- Last 15 Days / Last 30 Days — Rolling windows
- This Month / Last Month — Calendar months
- Last 6 Months / Last Year — Longer analysis
- Custom Range — Pick exact start and end dates

Important: When you search by phone number in Call Analysis, the date filter is automatically bypassed to search across ALL dates. You'll see a "Searching all dates" indicator.

Tip: If you can't find a call, try expanding the date range. Calls from yesterday won't show under "Today".`,
  },
  {
    id: "ai-pipeline",
    icon: Shield,
    title: "How AI Classification Works",
    content: `The AI pipeline processes each call through multiple stages:

Stage 1 — Transcription:
Gemini 2.5 Flash transcribes the audio (primary). If it fails, Whisper is used as fallback. If the transcript is suspiciously short for a long call, the system automatically re-transcribes.

Stage 2 — Text Analysis:
GPT-4o-mini analyzes ONLY the caller's speech. It ignores everything the agent, IVR, or system says. This prevents false positives from business greetings triggering spam flags.

Stage 3 — Post-Processing Safety Nets:
Regex-based keyword scanning catches adult content the AI missed. Word-count checks catch dead calls. Service-intent rescue prevents legitimate rental/cleaning calls from being flagged as buy/sell.

Stage 4 — Audio Accent Detection:
For calls classified as valid, Gemini listens to the actual audio to detect South Asian accents — even when the caller is trying to fake a US accent.

Stage 5 — Audio Adult Detection:
For restricted/anonymous callers or calls with very little speech, Gemini listens for non-verbal adult content (moaning, sounds) that wouldn't appear in a transcript.

Stage 6 — Dead Call Verification:
If a call is classified as dead but the transcript is suspiciously short, Gemini listens to verify whether the caller actually spoke. This catches transcription failures.

Priority Order:
Adult (highest) > Dead > IVR > Spam > Buy/Sell > Non-US Accent > Valid (lowest)`,
  },
  {
    id: "credits",
    icon: AlertTriangle,
    title: "Credits & Minutes",
    content: `Each call analysis uses credits (minutes). The admin account has unlimited credits. Regular users need to purchase minutes before they can analyze calls.

Buy Minutes:
Go to "Buy Minutes" in the sidebar to purchase analysis credits.

Cost per call depends on the length of the recording and how many AI stages are needed (transcription + analysis + optional audio checks).`,
  },
  {
    id: "settings",
    icon: Settings,
    title: "Settings (Admin)",
    content: `System-wide configuration:

Ringba Integration:
Your Ringba API key and Account ID are configured in your Profile page. These are required for call imports, caller blocking, and publisher pausing to work.

Auto-Import:
Calls are automatically imported from Ringba every 5 minutes. Only calls longer than 90 seconds are imported (shorter calls don't have enough content for meaningful analysis).

Action Settings:
Configure auto-blocking and auto-pausing thresholds in the Action Center settings.

Important: Changes made in the preview environment need to be DEPLOYED before they appear on your production app. Click the "Deploy" button on the Emergent platform after making changes.`,
  },
];

export default function SoftwareGuidePage() {
  const [expandedSection, setExpandedSection] = useState("overview");
  const [searchQuery, setSearchQuery] = useState("");

  const toggleSection = (id) => {
    setExpandedSection(expandedSection === id ? null : id);
  };

  const filteredSections = searchQuery
    ? sections.filter(
        (s) =>
          s.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
          s.content.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : sections;

  return (
    <div className="p-4 md:p-6 max-w-[1100px] mx-auto" data-testid="software-guide-page">
      {/* Header */}
      <div className="mb-6">
        <h1
          className="text-xl sm:text-2xl font-semibold text-white tracking-tight"
          style={{ fontFamily: "Outfit, sans-serif" }}
          data-testid="guide-title"
        >
          Software Guide
        </h1>
        <p className="text-xs sm:text-sm text-slate-500 mt-1">
          Everything you need to know about Callintell — features, filters, AI
          classification, and how to use each page.
        </p>
      </div>

      {/* Search */}
      <div className="mb-5">
        <div className="w-full max-w-md flex items-center gap-2 bg-[#0B0D12] border border-white/10 rounded-md px-3 h-9 focus-within:ring-1 focus-within:ring-blue-500/50 transition">
          <Search className="h-3.5 w-3.5 text-slate-500 shrink-0" />
          <input
            type="text"
            placeholder="Search guide..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="flex-1 bg-transparent text-sm text-white placeholder:text-slate-500 focus:outline-none"
            data-testid="guide-search"
          />
        </div>
      </div>

      {/* Sections */}
      <div className="space-y-2">
        {filteredSections.map((section) => {
          const isOpen = expandedSection === section.id;
          const Icon = section.icon;
          return (
            <div
              key={section.id}
              className="bg-[#11141D] border border-white/5 rounded-xl overflow-hidden"
              data-testid={`guide-section-${section.id}`}
            >
              <button
                onClick={() => toggleSection(section.id)}
                className="w-full flex items-center gap-3 px-4 py-3.5 text-left hover:bg-white/[0.02] transition-colors"
                data-testid={`guide-toggle-${section.id}`}
              >
                <div
                  className={`p-1.5 rounded-lg transition-colors ${
                    isOpen
                      ? "bg-blue-500/15 text-blue-400"
                      : "bg-white/5 text-slate-500"
                  }`}
                >
                  <Icon className="h-4 w-4" />
                </div>
                <span
                  className={`flex-1 text-sm font-medium transition-colors ${
                    isOpen ? "text-white" : "text-slate-300"
                  }`}
                >
                  {section.title}
                </span>
                {isOpen ? (
                  <ChevronDown className="h-4 w-4 text-slate-500" />
                ) : (
                  <ChevronRight className="h-4 w-4 text-slate-500" />
                )}
              </button>
              {isOpen && (
                <div className="px-4 pb-4 pt-0">
                  <div className="border-t border-white/5 pt-3">
                    {section.content.split("\n\n").map((paragraph, i) => {
                      // Check if paragraph is a heading (ends with :)
                      const lines = paragraph.split("\n");
                      return (
                        <div key={i} className="mb-3 last:mb-0">
                          {lines.map((line, j) => {
                            const trimmed = line.trim();
                            if (!trimmed) return null;

                            // Section heading (line ending with :)
                            if (
                              trimmed.endsWith(":") &&
                              trimmed.length < 60 &&
                              !trimmed.startsWith("-") &&
                              !trimmed.startsWith("•")
                            ) {
                              return (
                                <p
                                  key={j}
                                  className="text-sm font-semibold text-slate-200 mt-3 mb-1"
                                >
                                  {trimmed}
                                </p>
                              );
                            }

                            // Bullet points
                            if (
                              trimmed.startsWith("- ") ||
                              trimmed.startsWith("• ")
                            ) {
                              const text = trimmed.slice(2);
                              // Bold part before —
                              const dashIdx = text.indexOf(" — ");
                              if (dashIdx > 0) {
                                return (
                                  <div
                                    key={j}
                                    className="flex gap-2 text-sm text-slate-400 ml-2 mb-1"
                                  >
                                    <span className="text-slate-600 shrink-0">
                                      •
                                    </span>
                                    <span>
                                      <span className="text-slate-200 font-medium">
                                        {text.slice(0, dashIdx)}
                                      </span>
                                      <span className="text-slate-500">
                                        {" "}
                                        — {text.slice(dashIdx + 3)}
                                      </span>
                                    </span>
                                  </div>
                                );
                              }
                              return (
                                <div
                                  key={j}
                                  className="flex gap-2 text-sm text-slate-400 ml-2 mb-1"
                                >
                                  <span className="text-slate-600 shrink-0">
                                    •
                                  </span>
                                  <span>{text}</span>
                                </div>
                              );
                            }

                            // Numbered items
                            if (/^\d+\.\s/.test(trimmed)) {
                              const num = trimmed.match(/^(\d+)\.\s/)[1];
                              const text = trimmed.replace(/^\d+\.\s/, "");
                              return (
                                <div
                                  key={j}
                                  className="flex gap-2 text-sm text-slate-400 ml-2 mb-1"
                                >
                                  <span className="text-blue-400/60 font-mono text-xs shrink-0 mt-0.5">
                                    {num}.
                                  </span>
                                  <span>{text}</span>
                                </div>
                              );
                            }

                            // Keyword definition (Bold text followed by colon)
                            if (
                              /^[A-Z][\w\s/&()]+\(.*\):$/.test(trimmed) ||
                              /^[A-Z][\w\s/&]+:$/.test(trimmed)
                            ) {
                              return (
                                <p
                                  key={j}
                                  className="text-sm font-semibold text-slate-200 mt-3 mb-1"
                                >
                                  {trimmed}
                                </p>
                              );
                            }

                            // Tip lines
                            if (trimmed.startsWith("Tip:")) {
                              return (
                                <div
                                  key={j}
                                  className="flex items-start gap-2 mt-2 px-3 py-2 bg-blue-500/5 border border-blue-500/10 rounded-lg"
                                >
                                  <span className="text-blue-400 text-xs mt-0.5 shrink-0">
                                    TIP
                                  </span>
                                  <span className="text-sm text-blue-300/80">
                                    {trimmed.slice(5)}
                                  </span>
                                </div>
                              );
                            }

                            // Important lines
                            if (trimmed.startsWith("Important:")) {
                              return (
                                <div
                                  key={j}
                                  className="flex items-start gap-2 mt-2 px-3 py-2 bg-amber-500/5 border border-amber-500/10 rounded-lg"
                                >
                                  <span className="text-amber-400 text-xs mt-0.5 shrink-0">
                                    NOTE
                                  </span>
                                  <span className="text-sm text-amber-300/80">
                                    {trimmed.slice(11)}
                                  </span>
                                </div>
                              );
                            }

                            // Regular paragraph
                            return (
                              <p
                                key={j}
                                className="text-sm text-slate-400 leading-relaxed"
                              >
                                {trimmed}
                              </p>
                            );
                          })}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Footer */}
      <div className="mt-8 text-center text-xs text-slate-600">
        Callintell v1.0 — AI-Powered Call Intelligence for Pay Per Call
      </div>
    </div>
  );
}
